import { Component, HostBinding, inject, OnDestroy, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, ResolveEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { combineLatestWith, Subscription } from 'rxjs';
import { HistoryService, stripQueryParams } from '@cumlaude/shared-services';
import { get, isEmpty } from 'lodash-es';
import { RestLoggingService } from './services/rest.logging.service';
import { HttpCancelService } from './core/services/http-cancel-service';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { UserService } from './services/user.service';
import { ToastrService } from '@cumlaude/shared-components-toast';
import { messages } from './services/toastr-messages';
import { HamburgerMenuComponent } from '@cumlaude/shared-components-menu';
import { HeaderComponent } from './layout/header/header.component';
import { GtmService } from './services/gtm.service';
import { UrlService } from './services/url.service';
import { AiChatService } from './services/ai-chat.service';
import { UserMenuService } from './services/user-menu.service';
import { NavigatieVoorkeurService } from './services/navigatie-voorkeur.service';
import { NavigatieUitklapService } from './services/navigatie-uitklap.service';
import { HoofdnavigatieComponent } from './layout/hoofdnavigatie/hoofdnavigatie.component';
import { PaginakopComponent } from './layout/paginakop/paginakop.component';

declare global {
	interface Window {
		Appcues: any;
	}
}

/**
 * HistoryService moet in het hoofd component geinject worden zodat altijd de history getracked wordt tijdens het gebruiken van de applicatie.
 * En op die manier dan de terug knoppen kunnen werken.
 * Datzelfde geldt ook voor de aiChatService.
 */
@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	imports: [RouterOutlet, HamburgerMenuComponent, RouterLink, HeaderComponent, HoofdnavigatieComponent, PaginakopComponent],
})
export class AppComponent implements OnInit, OnDestroy {
	// noinspection JSUnusedGlobalSymbols
	readonly historyService = inject(HistoryService);
	readonly toastr = inject(ToastrService);
	readonly urlService = inject(UrlService);
	// noinspection JSUnusedGlobalSymbols
	readonly aiChatService = inject(AiChatService);
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);
	private readonly gtmService = inject(GtmService);
	private readonly httpCancelService = inject(HttpCancelService);
	private readonly router = inject(Router);
	private readonly userService = inject(UserService);
	protected readonly userMenuService = inject(UserMenuService);
	protected readonly navigatieVoorkeurService = inject(NavigatieVoorkeurService);
	private readonly uitklap = inject(NavigatieUitklapService);
	private readonly nieuweNavigatie$ = toObservable(this.navigatieVoorkeurService.nieuweNavigatie);

	/**
	 * Bepaalt de grid-layout van de shell, zie app.component.scss.
	 */
	@HostBinding('class.verticaal')
	get verticaal() {
		return this.navigatieVoorkeurService.nieuweNavigatie();
	}

	@HostBinding('class.ingeklapt')
	get ingeklapt() {
		return this.uitklap.ingeklapt();
	}

	private readonly subscriptions: Subscription[] = [];

	constructor() {
		const restLoggingService = inject(RestLoggingService);

		this.subscriptions.push(
			this.router.events
				.pipe(
					filter((event) => event instanceof ResolveEnd),
					map((event) => event.urlAfterRedirects)
				)
				.subscribe((url) => restLoggingService.currentDashboard.set(stripQueryParams(url))),

			this.router.events.subscribe((event) => {
				// Bij wegnavigeren naar ander dashboard of aanpassen van queryparams (incl filters) worden alle lopende requests gecancelled
				// door zowel frontend als backend.
				if (event instanceof NavigationStart) {
					this.httpCancelService.cancelPendingRequests(restLoggingService.currentDashboard(), stripQueryParams(event.url));
				}
			}),

			this.router.events.subscribe((event) => {
				if (event instanceof ResolveEnd) {
					const msg_key = (this.router.currentNavigation()?.extras?.info as { toastr?: string })?.toastr;
					if (msg_key) {
						const { type, message } = messages[msg_key];
						if (message) this.toastr.show(message, this.toastr.toastrConfig.iconClasses[type]);
					}
				}
			})
		);
	}

	ngOnInit() {
		this.gtmService.init();
		this.initAppcues();
		this.navigatieVoorkeurService.controleerBijStart();
	}

	ngOnDestroy() {
		this.subscriptions.forEach((subscription) => subscription.unsubscribe());
	}

	private initAppcues() {
		const { appcuesAccountId, environmentName } = this.envConfig;

		if (!isEmpty(appcuesAccountId)) {
			const scriptTag = document.createElement('script');
			scriptTag.src = `//fast.appcues.com/${appcuesAccountId}.js`;
			scriptTag.addEventListener('load', () => {
				this.addAppcuesEventListeners(environmentName);
			});
			const head = document.getElementsByTagName('head')[0];
			head.appendChild(scriptTag);
		}
	}

	private addAppcuesEventListeners(environmentName: string) {
		this.subscriptions.push(
			this.userService.myAccount$
				.pipe(combineLatestWith(this.userService.instelling$, this.nieuweNavigatie$))
				.subscribe(([account, instelling, nieuweNavigatie]) => {
					const appcues = get(window, 'Appcues');
					if (!appcues) return;
					appcues.identify(account.id, {
						naam: account.naam,
						support: account.support,
						rollen: account.rollen,
						omgeving: environmentName,
						nieuweNavigatie,
						navigatieKeuze: this.navigatieVoorkeurService.navigatieKeuze(),
					});

					appcues.group(instelling.id, {
						naam: instelling.naam,
						bron: instelling.bron,
						modules: instelling.modules,
					});
				}),
			this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
				get(window, 'Appcues')?.page();
			})
		);
	}
}
